import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button, Chip, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  ToggleButton, ToggleButtonGroup, Alert, IconButton
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, VideoCall, Check, Close
} from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import { getTeacherBookings, confirmBooking, cancelBooking } from '../../api/booking';
import { setTeacherAvailability } from '../../api/marketplace';
import { getDateInTimezone, getTimeInTimezone } from '../../utils/timezone';

const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TIME_SLOTS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00',
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00'
];

const TeacherCalendar = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('bookings'); // bookings | availability
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    const start = new Date(now);
    start.setDate(now.getDate() - day);
    return start;
  });
  const [availability, setAvailability] = useState({});
  const [savingAvail, setSavingAvail] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    loadBookings();
  }, [user?.id]);

  const loadBookings = async () => {
    if (!user?.id) return;
    try {
      const data = await getTeacherBookings(user.id);
      setBookings(data);
    } catch (err) {
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (bookingId) => {
    try {
      await confirmBooking(bookingId);
      await loadBookings();
      setSelectedBooking(null);
    } catch (err) {
      console.error('Error confirming:', err);
    }
  };

  const handleCancel = async (bookingId) => {
    try {
      await cancelBooking(bookingId, 'Teacher cancelled');
      await loadBookings();
      setSelectedBooking(null);
    } catch (err) {
      console.error('Error cancelling:', err);
    }
  };

  const toggleAvailability = (day, time) => {
    const key = `${day}-${time}`;
    setAvailability(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const saveAvailability = async () => {
    setSavingAvail(true);
    try {
      const slots = Object.entries(availability)
        .filter(([_, active]) => active)
        .map(([key]) => {
          const [day, time] = key.split('-');
          const timeIndex = TIME_SLOTS.indexOf(time);
          return {
            dayOfWeek: parseInt(day),
            startTime: time,
            endTime: TIME_SLOTS[timeIndex + 1] || '22:00'
          };
        });
      await setTeacherAvailability(user.id, slots);
      alert('Availability saved!');
    } catch (err) {
      console.error('Error saving availability:', err);
    } finally {
      setSavingAvail(false);
    }
  };

  const navigateWeek = (direction) => {
    setCurrentWeekStart(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + (direction * 7));
      return newDate;
    });
  };

  const getWeekDays = () => {
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      return date;
    });
  };

  // Returns the teacher's IANA timezone, falling back to the browser's.
  const teacherTz = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Display helpers — use UTC timestamp when present, fall back to legacy strings.
  const bDate = (b) =>
    b.startAtUTC ? getDateInTimezone(b.startAtUTC, teacherTz) : b.date;
  const bStartTime = (b) =>
    b.startAtUTC ? getTimeInTimezone(b.startAtUTC, teacherTz) : b.startTime;
  const bEndTime = (b) =>
    b.endAtUTC ? getTimeInTimezone(b.endAtUTC, teacherTz) : b.endTime;

  const getBookingsForDate = (date) => {
    const dateStr = toLocalDateStr(date);
    return bookings.filter(b => bDate(b) === dateStr);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'completed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  const weekDays = getWeekDays();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Calendar</Typography>
        <ToggleButtonGroup
          value={view} exclusive
          onChange={(_, v) => v && setView(v)} size="small"
        >
          <ToggleButton value="bookings">Bookings</ToggleButton>
          <ToggleButton value="availability">Set Availability</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {view === 'bookings' ? (
        <>
          {/* Week Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
            <IconButton onClick={() => navigateWeek(-1)}><ChevronLeft /></IconButton>
            <Typography variant="h6">
              {weekDays[0].toLocaleDateString('en', { month: 'long', day: 'numeric' })} - {weekDays[6].toLocaleDateString('en', { month: 'long', day: 'numeric', year: 'numeric' })}
            </Typography>
            <IconButton onClick={() => navigateWeek(1)}><ChevronRight /></IconButton>
          </Box>

          {/* Week Grid */}
          <Grid container spacing={1}>
            {weekDays.map((date, i) => {
              const dayBookings = getBookingsForDate(date);
              const isToday = new Date().toDateString() === date.toDateString();
              return (
                <Grid item xs={12} sm={6} md key={i}>
                  <Paper
                    sx={{
                      p: 1.5, minHeight: 200,
                      border: isToday ? '2px solid #2196F3' : '1px solid #e0e0e0',
                      bgcolor: isToday ? '#E3F2FD' : 'white'
                    }}
                  >
                    <Typography
                      variant="subtitle2" fontWeight={600} textAlign="center"
                      color={isToday ? 'primary.main' : 'text.primary'}
                    >
                      {DAYS[date.getDay()].slice(0, 3)}
                    </Typography>
                    <Typography variant="h6" textAlign="center" fontWeight={700} sx={{ mb: 1 }}>
                      {date.getDate()}
                    </Typography>

                    {dayBookings.map(booking => (
                      <Card
                        key={booking.id}
                        sx={{
                          mb: 0.5, cursor: 'pointer',
                          '&:hover': { boxShadow: 2 }
                        }}
                        onClick={() => setSelectedBooking(booking)}
                      >
                        <CardContent sx={{ p: '8px !important' }}>
                          <Typography variant="caption" fontWeight={600} noWrap>
                            {bStartTime(booking)} {booking.studentName}
                          </Typography>
                          <Chip
                            label={booking.status} size="small"
                            color={getStatusColor(booking.status)}
                            sx={{ height: 18, fontSize: 10, mt: 0.5 }}
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* All bookings list */}
          <Paper sx={{ mt: 3, p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>All Bookings</Typography>
            {bookings.length === 0 ? (
              <Typography color="text.secondary">No bookings yet</Typography>
            ) : (
              bookings.map(b => (
                <Box
                  key={b.id}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: 2, mb: 1, bgcolor: '#f8f9fa', borderRadius: 2, cursor: 'pointer',
                    '&:hover': { bgcolor: '#e8e8e8' }
                  }}
                  onClick={() => setSelectedBooking(b)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar>{b.studentName?.charAt(0)}</Avatar>
                    <Box>
                      <Typography fontWeight={600}>{b.studentName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {bDate(b)} · {bStartTime(b)} - {bEndTime(b)} · {b.language}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={`$${b.price}`} size="small" variant="outlined" />
                    <Chip label={b.status} size="small" color={getStatusColor(b.status)} />
                    {b.isTrial && <Chip label="Trial" size="small" color="info" />}
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </>
      ) : (
        /* Availability View */
        <Paper sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Click on time slots to mark when you're available for lessons. Green = available.
          </Alert>
          <Box sx={{ overflow: 'auto' }}>
            <Grid container spacing={0}>
              <Grid item sx={{ minWidth: 80 }}>
                <Box sx={{ height: 40 }} />
                {TIME_SLOTS.map(time => (
                  <Box key={time} sx={{ height: 40, display: 'flex', alignItems: 'center', pr: 1 }}>
                    <Typography variant="caption" color="text.secondary">{time}</Typography>
                  </Box>
                ))}
              </Grid>
              {DAYS.map((day, dayIndex) => (
                <Grid item xs key={dayIndex} sx={{ minWidth: 100 }}>
                  <Box sx={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="subtitle2" fontWeight={600}>{day.slice(0, 3)}</Typography>
                  </Box>
                  {TIME_SLOTS.map(time => {
                    const key = `${dayIndex}-${time}`;
                    const active = availability[key];
                    return (
                      <Box
                        key={time}
                        onClick={() => toggleAvailability(dayIndex, time)}
                        sx={{
                          height: 40, border: '1px solid #e0e0e0', cursor: 'pointer',
                          bgcolor: active ? '#4caf50' : 'white',
                          '&:hover': { bgcolor: active ? '#388e3c' : '#e8e8e8' },
                          transition: 'all 0.15s'
                        }}
                      />
                    );
                  })}
                </Grid>
              ))}
            </Grid>
          </Box>
          <Button
            variant="contained" sx={{ mt: 3 }} onClick={saveAvailability}
            disabled={savingAvail}
            startIcon={savingAvail && <CircularProgress size={20} />}
          >
            {savingAvail ? 'Saving...' : 'Save Availability'}
          </Button>
        </Paper>
      )}

      {/* Booking Detail Dialog */}
      <Dialog open={!!selectedBooking} onClose={() => setSelectedBooking(null)} maxWidth="sm" fullWidth>
        {selectedBooking && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>Booking Details</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Student</Typography>
                  <Typography fontWeight={600}>{selectedBooking.studentName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Language</Typography>
                  <Typography fontWeight={600}>{selectedBooking.language}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Date</Typography>
                  <Typography fontWeight={600}>{bDate(selectedBooking)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Time</Typography>
                  <Typography fontWeight={600}>{bStartTime(selectedBooking)} - {bEndTime(selectedBooking)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Price</Typography>
                  <Typography fontWeight={600}>${selectedBooking.price}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip label={selectedBooking.status} color={getStatusColor(selectedBooking.status)} size="small" />
                </Grid>
                {selectedBooking.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Notes</Typography>
                    <Typography>{selectedBooking.notes}</Typography>
                  </Grid>
                )}
                {selectedBooking.zoomLink && (
                  <Grid item xs={12}>
                    <Button
                      variant="contained" startIcon={<VideoCall />} fullWidth
                      onClick={() => window.open(selectedBooking.zoomLink, '_blank')}
                    >
                      Join Video Call
                    </Button>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              {selectedBooking.status === 'pending' && (
                <>
                  <Button
                    color="error" startIcon={<Close />}
                    onClick={() => handleCancel(selectedBooking.id)}
                  >
                    Decline
                  </Button>
                  <Button
                    variant="contained" color="success" startIcon={<Check />}
                    onClick={() => handleConfirm(selectedBooking.id)}
                  >
                    Confirm & Generate Link
                  </Button>
                </>
              )}
              <Button onClick={() => setSelectedBooking(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default TeacherCalendar;
